import { Footer } from '@/features/landing/sections/Footer'
import { Nav } from '@/features/landing/sections/Nav'
import { ClosingCTA } from '@/features/marketing/components/ClosingCTA'
import { ExploreHero } from '@/features/marketing/components/ExploreHero'
import { FeatureBlock } from '@/features/marketing/components/FeatureBlock'
import { MockupFrame } from '@/features/marketing/components/MockupFrame'
import { LiveTabMockup } from '@/features/marketing/mockups/LiveTabMockup'
import { MultiStreamMockup } from '@/features/marketing/mockups/MultiStreamMockup'
import { StreamHeroMockup } from '@/features/marketing/mockups/StreamHeroMockup'
import { TwitchLinkMockup } from '@/features/marketing/mockups/TwitchLinkMockup'

/**
 * /explore/live — streaming features deep-dive.
 *
 * Four feature blocks:
 *   1. In-app Live tab            — "see who's live in your squadron"
 *   2. Twitch link + auto-detect  — "no manual 'i'm live now' button"
 *   3. Stream events              — "schedule + auto-flip to live"
 *   4. Tournament multi-stream    — "watch every POV from one URL"
 *
 * Visual lead: a featured stream tile + chat sidebar so the
 * embedded-player promise lands within the hero.
 */
export default function ExploreLive() {
  return (
    <div className="min-h-dvh bg-bg text-fg">
      <Nav />
      <main>
        <ExploreHero
          bucketLabel="Live"
          headline={
            <>
              Your squadron's<br />
              live tab. Built in.
            </>
          }
          subline="Link a Twitch handle once. When someone in your squadron goes live, their tile flips on automatically — visible to every member from the in-app Live tab. No Discord pings, no copy-pasted URLs."
          secondaryCtaLabel="See tournaments"
          secondaryCtaTo="/explore/tournaments"
          visual={
            <MockupFrame address="e404.app / stream / orion_blk" className="max-w-xl">
              <StreamHeroMockup />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="01 · In-app Live tab"
          headline={
            <>
              Who's live, what they're playing,<br />
              and how to join — at a glance.
            </>
          }
          copy={
            <>
              Every squadron gets a Live tab. Cards show the current
              streamer, the game they're in, viewer count, and the
              embedded player one tap away. No Discord-pings, no
              context-switch to twitch.tv. Built for the moment
              someone in your group says "anyone on?"
            </>
          }
          bullets={[
            'Auto-refreshes every 60 seconds — never shows a stale live state',
            'Game label pulled live from Twitch metadata (no manual updates)',
            'Tap a tile to open the embedded player without leaving e404',
            'Sort by viewer count, game, or squadron rank',
          ]}
          visual={
            <MockupFrame address="e404.app / squadron / live" className="max-w-xl">
              <LiveTabMockup />
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="02 · Twitch link · auto-detect"
          headline="Connect once. Stream as normal."
          copy={
            <>
              Members link their Twitch handle from their profile — a
              standard OAuth flow, no scary scopes. From that point on,
              e404 polls Twitch's public live status and flips the
              tile on the second a stream goes up. Stream as you
              normally would; the squadron just knows.
            </>
          }
          bullets={[
            'Standard Twitch OAuth — read-only public scopes, revokable from profile',
            "Polls live status every 60s — no webhooks for the member to set up",
            'Works with mobile streams (Twitch IRL) and desktop OBS equally',
            'YouTube and Kick support on the roadmap — Twitch ships first',
          ]}
          visual={
            <MockupFrame address="e404.app / profile / linked-accounts" className="max-w-md">
              <TwitchLinkMockup />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="03 · Stream events"
          headline={
            <>
              Schedule a stream night.<br />
              Members get notified the second it starts.
            </>
          }
          copy={
            <>
              Create a STREAM event — same flow as a gaming session or
              tournament. RSVP'd members get a push notification when
              the stream actually flips live (not when you said it
              would start; when it really does). The event tile in
              the squadron feed auto-promotes the live player into
              the calendar entry.
            </>
          }
          bullets={[
            'STREAM event type — slots into the same Events tab as everything else',
            'Push notification triggered by Twitch live signal, not the schedule',
            'Recurring weekly streams supported — "Friday CS night, every week"',
            'Auto-attaches the live player to the event page until the stream ends',
          ]}
          visual={
            <MockupFrame
              address="e404.app / event / friday-cs-night"
              className="max-w-md"
            >
              <div className="space-y-3 p-5">
                <div className="rounded-xl border border-border bg-bg/30 p-4">
                  <p className="font-display text-[10px] font-bold tracking-[0.2em] text-primary uppercase">
                    Stream · Recurring · Fri 20:00 BST
                  </p>
                  <h3 className="font-display mt-2 text-base font-bold text-fg">
                    Friday CS night
                  </h3>
                  <p className="mt-1 text-[11px] text-fg-muted">
                    4 streamers · 12 RSVPs · streams open ~20:00
                  </p>
                  <div className="mt-3 flex items-center gap-1.5">
                    <span className="font-display rounded-sm bg-success/15 px-1.5 py-[1px] text-[9px] font-extrabold tracking-[0.18em] text-success uppercase">
                      <span className="mr-1 inline-block size-1 rounded-full bg-success align-middle" />
                      Live now
                    </span>
                    <span className="font-display text-[10px] text-fg-muted">
                      @orion_blk · 247 watching
                    </span>
                  </div>
                </div>
                <div className="rounded-lg border border-border/60 bg-bg/40 p-3">
                  <p className="font-display text-[10px] font-extrabold tracking-[0.2em] text-fg-subtle uppercase">
                    Notification sent
                  </p>
                  <p className="mt-1 text-[11px] text-fg-muted">
                    "<span className="font-display font-bold text-fg">@orion_blk</span> just went
                    live for Friday CS night" — pushed to 11 RSVPs · 18s ago
                  </p>
                </div>
              </div>
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="04 · Tournament multi-stream"
          headline={
            <>
              Watch every POV<br />
              from one URL.
            </>
          }
          copy={
            <>
              When your tournament has 4-8 captains streaming their own
              POV, the multi-stream view stacks them into a 2x2 grid
              on the venue TV (or any member's browser). Audio mutes
              to the focused tile; click to swap. Pair it with the
              Tournament Stadium for a full broadcast experience.
            </>
          }
          bullets={[
            'Up to 8 simultaneous POVs in a single grid',
            'Tap to focus — audio swaps to the focused tile',
            'Public 24-hour signed URL — share without sign-in',
            'Pairs with Tournament Stadium: bracket + multi-stream side by side',
          ]}
          visual={
            <MockupFrame
              address="e404.app / stadium / multi-stream"
              className="max-w-2xl"
            >
              <MultiStreamMockup />
            </MockupFrame>
          }
        />

        {/* Comparison strip */}
        <section className="border-b border-border/40 bg-surface/30 px-6 py-20">
          <div className="mx-auto max-w-5xl">
            <p className="font-display text-center text-xs font-bold tracking-[0.3em] text-fg-subtle uppercase">
              How e404 stacks up
            </p>
            <h2 className="font-display mt-4 text-center text-3xl leading-tight font-bold md:text-4xl">
              You shouldn't need three apps to know who's streaming.
            </h2>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {[
                {
                  name: 'Discord "Now streaming" role',
                  takeaway:
                    "Half the squadron has Discord's rich presence switched off, so the streaming role lies about as often as it tells the truth, the ping still floods the channel when it does fire, and you end up opening a second tab to watch anyway.",
                },
                {
                  name: 'Twitch teams',
                  takeaway:
                    "Twitch teams look good to outside viewers, but there's no roster underneath it and no way to hang a recurring Friday stream night off the same page.",
                },
                {
                  name: 'StreamElements / Restream',
                  takeaway:
                    "StreamElements and Restream are built for the person behind the camera, so your squadron's tournament bracket and roster never make it into the overlay.",
                },
              ].map((c) => (
                <div
                  key={c.name}
                  className="rounded-xl border border-border bg-bg/40 p-5"
                >
                  <p className="font-display text-[10px] font-bold tracking-[0.25em] text-fg-subtle uppercase">
                    Versus
                  </p>
                  <h3 className="font-display mt-2 text-lg font-bold text-fg">
                    {c.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-fg-muted">
                    {c.takeaway}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClosingCTA
          location="live"
          headline="Plug Twitch in. Stop pinging links in Discord."
          subline="Free for unlimited stream events. The Live tab is part of every squadron tier."
          primaryCtaLabel="Get started free"
          primaryCtaTo="/signup"
        />
      </main>
      <Footer />
    </div>
  )
}
